/*
  KAAL — page behaviour.

  Journey: the product and origin pages are a series of places in the KAAL universe.
  One scroll (mouse, trackpad), one swipe (phone) or an arrow key travels to the next
  place: the one you leave grows and fades past you, the next comes forward out of the
  depth, through a soft burst of light. Every move has the same smooth, unhurried pace,
  however fast the wheel spins.

  Past the last place the page simply carries on, like any page, down to the newsletter and footer.

  Warp: links to another KAAL page dissolve into light, and the next page emerges from it.
*/
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
  const DUR = reduce ? 450 : 1050;                     // one move between places
  const EASE = 'cubic-bezier(.65,0,.35,1)';            // gentle start and arrival
  const NEAR = .84, FAR = 1.28;                        // depth: coming forward from .84, passing by at 1.28

  /* ---------- light veil, shared by scene moves and page warps ---------- */
  let veil;
  const getVeil = () => {
    if (!veil) { veil = document.createElement('div'); veil.className = 'k-veil'; veil.setAttribute('aria-hidden', 'true'); document.body.append(veil); }
    return veil;
  };

  /* ---------- journey (product + origin pages) ---------- */
  const initJourney = () => {
    const scenes = [...document.querySelectorAll('.shopify-section.scene')];
    if (!scenes.length) return null;
    document.documentElement.classList.add('k-journey');
    const inner = (s) => s.querySelector('.scene__inner');
    const anchor = (s) => inner(s)?.dataset.anchor || '';
    // after the last place the page goes on (newsletter + footer) and scrolls like any page
    const root = document.documentElement;
    const endEl = document.querySelector('[data-k-end]');
    const last = scenes.length - 1;
    let atEnd = false, freeDown = false;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const toTop = () => { if (scrollY) scrollTo(0, 0); };


    // first-screen hint
    const hint = document.createElement('div');
    hint.className = 'k-hint'; hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = `<span>${finePointer ? 'Scroll' : 'Swipe'}</span><i></i>`;
    if (scenes.length > 1) document.body.append(hint);

    let cur = -1, busy = false, pending = 0, moveStart = -1e9;

    const mark = (i) => {
      cur = i;
      const s = scenes[i];
      document.body.dataset.tone = inner(s)?.dataset.tone || 'dark';
      document.body.dataset.hint = inner(s)?.dataset.hint || document.body.dataset.tone;
      document.body.dataset.nav = inner(s)?.dataset.nav || document.body.dataset.tone;
      atEnd = !!endEl && i === last; freeDown = false;
      root.classList.toggle('k-at-end', atEnd);
      scenes.forEach((x, j) => { x.inert = j !== i; });
      const a = anchor(s);
      history.replaceState(null, '', a ? `#${a}` : location.pathname + location.search);
      if (i > 0) hint.classList.add('is-gone');
    };

    // jump straight to a place (first load, links, theme editor)
    const show = (i, entrance) => {
      scenes.forEach((s, j) => {
        s.getAnimations({ subtree: true }).forEach((a) => a.cancel());
        s.classList.toggle('is-on', j === i);
        s.classList.remove('in', 'is-out');
      });
      mark(i);
      const s = scenes[i];
      if (entrance && !reduce) {
        inner(s).animate([{ transform: 'scale(1.08)' }, { transform: 'scale(1)' }], { duration: 2200, easing: 'cubic-bezier(.2,.7,.2,1)' });
      }
      raf2(() => s.classList.add('in'));
    };

    // travel to a place
    const go = (i) => {
      if (busy || i === cur || i < 0 || i >= scenes.length) return false;
      if (reduce) { show(i); return true; }
      busy = true; moveStart = performance.now();
      const from = scenes[cur], to = scenes[i], fwd = i > cur;
      to.classList.remove('in', 'is-out');
      to.classList.add('is-on', 'is-entering');
      from.classList.add('is-out');
      const opts = { duration: DUR, easing: EASE, fill: 'both' };
      const a1 = inner(from).animate([{ transform: 'scale(1)' }, { transform: `scale(${fwd ? FAR : NEAR})` }], opts);
      const a2 = from.animate([{ opacity: 1 }, { opacity: 0 }], { duration: DUR * .8, delay: DUR * .2, easing: 'ease-in', fill: 'both' });
      const a3 = inner(to).animate([{ transform: `scale(${fwd ? NEAR : FAR})` }, { transform: 'scale(1)' }], opts);
      const a4 = to.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DUR * .7, delay: DUR * .12, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'both' });
      getVeil().animate([{ opacity: 0 }, { opacity: .24, offset: .48 }, { opacity: 0 }], { duration: DUR, easing: 'ease-in-out' });
      setTimeout(() => to.classList.add('in'), DUR * .45);
      mark(i);
      Promise.all([a3.finished, a4.finished]).then(() => {
        from.classList.remove('is-on', 'is-out', 'in');
        to.classList.remove('is-entering');
        [a1, a2, a3, a4].forEach((a) => a.cancel());
        busy = false;
        // a scroll made while this move was still playing goes next
        if (pending) { const d = pending; pending = 0; step(d); }
      });
      return true;
    };
    // one place along; past the last place, the rest of that scroll carries on down the page
    const step = (d) => {
      if (d > 0 && atEnd) { freeDown = true; return; }
      go(cur + d);
    };
    // one intent = one move. A scroll made in the second half of a move is remembered and
    // plays right after it; one made earlier is the same gesture still going, and is dropped.
    const request = (d) => {
      if (!busy) { step(d); return; }
      if (performance.now() - moveStart > DUR * .5) pending = d;
    };

    // wheel / trackpad: one scroll = one move.
    // Once a scroll has moved us, everything that follows belongs to that same scroll — a long
    // two-finger drag that speeds up and slows down, or the glide after a flick — until either
    //   • there is a clear pause, or
    //   • the motion has all but stopped (a glide fading out) and then a new push starts.
    // A mouse wheel's separate clicks count one each.
    let lastT = 0, lastM = 0, armT = 0, acc = 0, armed = true, low = Infinity;
    addEventListener('wheel', (e) => {
      if (e.ctrlKey) return;                                   // pinch-zoom on trackpads
      // past the last place the page scrolls on to the newsletter and footer: plain scrolling there.
      // A new scroll down starts it; the tail of the scroll that arrived here doesn't.
      if (atEnd && !busy) {
        const t = e.timeStamp || performance.now();
        if (scrollY > 0 || (e.deltaY > 0 && (freeDown || t - lastT > 300))) {
          if (e.deltaY > 0) freeDown = true;
          lastT = t; lastM = Math.abs(e.deltaY); armed = false; low = Infinity; acc = 0;
          return;
        }
      }
      e.preventDefault();
      let d = e.deltaY;
      if (e.deltaMode === 1) d *= 32; else if (e.deltaMode === 2) d *= innerHeight;
      if (Math.abs(e.deltaX) > Math.abs(d)) return;
      // time the scroll was made (not when we got to it — a busy frame can hold events back)
      const now = e.timeStamp || performance.now(), gap = now - lastT, m = Math.abs(d);
      // a clear pause means a new scroll — but a held-back glide keeps shrinking, so it doesn't count
      const fresh = gap > 300 || (gap > 200 && m >= lastM * .95 && m >= 8);
      lastT = now; lastM = m;
      if (fresh) { acc = 0; armed = true; armT = now; low = Infinity; }
      if (!armed) {
        if (m < low) low = m;
        if (low <= 2 && m >= 12) { armed = true; armT = now; acc = 0; low = Infinity; }
        else return;
      }
      // a real push gets going fast; a slow drift that never does is just the tail of a glide
      if (now - armT > 240) { armed = false; low = Infinity; acc = 0; return; }
      acc += d;
      if (Math.abs(acc) >= 18) {
        const dir = Math.sign(acc);
        acc = 0; armed = false; low = Infinity;
        request(dir);
      }
    }, { passive: false });

    // touch: swipe up / down
    // past the last place a drag up scrolls the page on; once it is scrolled, touches just scroll it
    let ty = null, tx = 0, tt = 0, free = false;
    addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { ty = null; return; }
      ty = e.touches[0].clientY; tx = e.touches[0].clientX; tt = performance.now();
      free = atEnd && scrollY > 0;
    }, { passive: true });
    addEventListener('touchmove', (e) => {
      if (ty === null || e.touches.length !== 1) return;
      if (!free && atEnd && !busy) {
        const dy = e.touches[0].clientY - ty;
        if (Math.abs(dy) < 6) return;                          // which way? not yet clear
        if (dy < 0) free = true;
      }
      if (!free && e.cancelable) e.preventDefault();
    }, { passive: false });
    addEventListener('touchend', (e) => {
      if (ty === null) return;
      const t = e.changedTouches[0], dy = ty - t.clientY, dx = tx - t.clientX, fast = performance.now() - tt < 350;
      ty = null;
      if (free) return;
      if (Math.abs(dy) > Math.abs(dx) && (Math.abs(dy) > 50 || (fast && Math.abs(dy) > 24))) request(dy > 0 ? 1 : -1);
    }, { passive: true });

    // keys
    addEventListener('keydown', (e) => {
      if (e.target.closest?.('input, textarea, select, [contenteditable], [data-k-end]')) return;
      const k = e.key;
      // past the last place the keys scroll the page as usual
      if (atEnd && (scrollY > 0 || k === 'ArrowDown' || k === 'PageDown' || k === 'End' || (k === ' ' && !e.shiftKey))) return;
      if (k === 'ArrowDown' || k === 'PageDown' || (k === ' ' && !e.shiftKey)) { e.preventDefault(); request(1); }
      else if (k === 'ArrowUp' || k === 'PageUp' || (k === ' ' && e.shiftKey)) { e.preventDefault(); request(-1); }
      else if (k === 'Home') { e.preventDefault(); go(0); }
      else if (k === 'End') { e.preventDefault(); go(last); }
    });

    // links to a place on this page
    const indexOf = (id) => scenes.findIndex((s) => anchor(s) === id);
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const u = new URL(a.href, location.href);
      if (u.pathname === location.pathname && u.search === location.search && u.hash) {
        const i = indexOf(decodeURIComponent(u.hash.slice(1)));
        if (i >= 0) { e.preventDefault(); toTop(); go(i); }
      }
    });

    // theme editor: show the section being edited
    document.addEventListener('shopify:section:select', (e) => {
      const id = `shopify-section-${e.detail.sectionId}`;
      const i = scenes.findIndex((s) => s.id === id);
      if (i >= 0) { toTop(); show(i); }
      else if (endEl && endEl.closest('.shopify-section')?.id === id) { show(last); requestAnimationFrame(() => endEl.scrollIntoView()); }
    });

    // gentle depth: the artwork drifts a little with the mouse
    if (finePointer && !reduce) {
      let r = 0, mx = 0, my = 0;
      addEventListener('mousemove', (e) => {
        mx = e.clientX / innerWidth - .5; my = e.clientY / innerHeight - .5;
        if (!r) r = requestAnimationFrame(() => {
          r = 0;
          document.documentElement.style.setProperty('--mx', mx.toFixed(3));
          document.documentElement.style.setProperty('--my', my.toFixed(3));
        });
      });
    }

    const start = location.hash ? indexOf(decodeURIComponent(location.hash.slice(1))) : -1;
    scrollTo(0, 0);
    show(start >= 0 ? start : 0, true);
    document.documentElement.classList.add('k-ready');
    return { scenes, inner };
  };

  /* ---------- home: entrance, light seam, mouse drift ---------- */
  const initHome = () => {
    const home = document.querySelector('[data-kaal-home]');
    if (!home) return;
    raf2(() => home.classList.add('is-ready'));
    const bg = home.querySelector('[data-bg]');
    // the seam sits at x=1100, meeting the floor at y=728, in the 1672×941 artwork
    const placeBeam = () => {
      const w = bg.clientWidth, h = bg.clientHeight, s = Math.max(w / 1672, h / 941);
      const px = matchMedia('(max-aspect-ratio: 5/4)').matches ? .76 : .5;   // the artwork's background-position x
      bg.style.setProperty('--beam-x', ((w - 1672 * s) * px + 1100 * s) + 'px');
      bg.style.setProperty('--beam-h', ((h - 941 * s) / 2 + 728 * s) + 'px');
    };
    placeBeam(); addEventListener('resize', placeBeam);
    if (!reduce && finePointer) {
      let r = 0, mx = 0, my = 0;
      addEventListener('mousemove', (e) => {
        mx = e.clientX / innerWidth - .5; my = e.clientY / innerHeight - .5;
        if (!r) r = requestAnimationFrame(() => { r = 0; bg.style.setProperty('--mx', mx.toFixed(3)); bg.style.setProperty('--my', my.toFixed(3)); });
      });
    }
  };

  /* ---------- warp between pages: into the light, out of the light ---------- */
  const WARP_KEY = 'kaal-warp';
  const initWarp = (journey) => {
    const v = getVeil();
    // arriving through the light
    let arrived = false;
    try { arrived = sessionStorage.getItem(WARP_KEY) === '1'; sessionStorage.removeItem(WARP_KEY); } catch (e) {}
    if (arrived && !reduce) {
      v.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 1100, easing: 'cubic-bezier(.3,0,.2,1)', fill: 'both' });
    }

    // leaving through the light
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (a.target && a.target !== '_self') return;
      const u = new URL(a.href, location.href);
      if (u.origin !== location.origin) return;
      if (u.pathname === location.pathname && u.search === location.search) return;   // same page: handled by the journey
      if (reduce) return;
      e.preventDefault();
      // zoom toward the light: the home seam, or the centre of the current place
      const home = document.querySelector('[data-kaal-home]');
      const target = home || document.querySelector('.shopify-section.scene.is-on .scene__inner');
      if (target) {
        let origin = '50% 50%';
        if (home) {
          const bx = parseFloat(home.querySelector('[data-bg]').style.getPropertyValue('--beam-x')) || innerWidth * .66;
          origin = `${bx}px 62%`;
        }
        target.style.transformOrigin = origin;
        target.animate([{ transform: 'scale(1)' }, { transform: `scale(${home ? 1.9 : 1.25})` }], { duration: 800, easing: 'cubic-bezier(.6,0,.8,.6)', fill: 'forwards' });
      }
      v.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 700, delay: 120, easing: 'ease-in', fill: 'forwards' });
      try { sessionStorage.setItem(WARP_KEY, '1'); } catch (err) {}
      setTimeout(() => { location.href = u.href; }, 820);
    });

    // coming back with the browser's back button: clear the light
    addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      v.getAnimations().forEach((x) => x.cancel());
      document.querySelectorAll('[data-kaal-home], .scene__inner').forEach((el) => el.getAnimations().forEach((x) => x.cancel()));
    });
  };

  /* ---------- film: play a video if one is set ---------- */
  const initFilm = () => {
    document.querySelectorAll('[data-play]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        const src = btn.dataset.video;
        if (!src) { btn.animate([{ scale: 1 }, { scale: .92 }, { scale: 1 }], { duration: 380, easing: 'ease-out' }); return; }
        const box = btn.closest('.scene__copy').querySelector('[data-video]');
        box.innerHTML = '';
        const v = document.createElement('video');
        Object.assign(v, { src, controls: true, autoplay: true, playsInline: true });
        const close = document.createElement('button');
        close.type = 'button'; close.textContent = '×'; close.setAttribute('aria-label', 'Close');
        close.addEventListener('click', () => { box.classList.remove('is-on'); box.innerHTML = ''; });
        box.append(v, close); box.classList.add('is-on');
      });
    });
  };

  const init = () => { const j = initJourney(); initHome(); initWarp(j); initFilm(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  // theme editor: re-run after a section is edited
  document.addEventListener('shopify:section:load', () => { initHome(); initFilm(); });
})();
